import * as React from 'react';

import { IconArrowForwardIos, IconMoreHoriz } from '@genesis/icons';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/app/utils/cn';

const Breadcrumb = ({ ...props }: React.ComponentProps<'nav'>) => {
  return <nav aria-label='breadcrumb' data-slot='breadcrumb' {...props} />;
};

const BreadcrumbList = ({ className, ...props }: React.ComponentProps<'ol'>) => {
  return (
    <ol
      data-slot='breadcrumb-list'
      className={cn(
        'text-content-neutral-secondary flex flex-wrap items-center gap-1.5 text-sm wrap-break-word sm:gap-2.5',
        className,
      )}
      {...props}
    />
  );
};

const BreadcrumbItem = ({ className, ...props }: React.ComponentProps<'li'>) => {
  return (
    <li
      data-slot='breadcrumb-item'
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
};

const BreadcrumbLink = ({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
}) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      data-slot='breadcrumb-link'
      className={cn('hover:text-content-neutral-primary transition-colors', className)}
      {...props}
    />
  );
};

const BreadcrumbPage = ({
  className,
  ...props
}: React.ComponentProps<'span'> & { children: React.ReactNode }) => {
  return (
    <span
      data-slot='breadcrumb-page'
      aria-disabled='true'
      aria-current='page'
      className={cn('text-content-neutral-primary font-normal capitalize', className)}
      {...props}
    />
  );
};

const BreadcrumbSeparator = ({ children, className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      data-slot='breadcrumb-separator'
      aria-hidden='true'
      className={cn('flex items-center justify-center', className)}
      {...props}
    >
      {children ?? <IconArrowForwardIos className='size-3.5 fill-content-neutral-primary' />}
    </span>
  );
};

const BreadcrumbEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      data-slot='breadcrumb-ellipsis'
      aria-hidden='true'
      className={cn('flex items-center justify-center', className)}
      {...props}
    >
      \
      <IconMoreHoriz className='size-4' />
      <span className='sr-only'>More</span>
    </span>
  );
};

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
